import React from "react";
import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LoginPage from "./pages/auth/LoginPage";
import { Route, Routes } from "react-router-dom";
import LandingPageWrapper from "./features/landing/LandingPageWrapper";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import FileUploadPage from "./pages/workspace/FileUploadPage";
import HomePage from "./pages/workspace/WorkspacePage";
import MyProfilePage from "./pages/me/MyProfilePage";
import { GlobalStyle } from "./styles/GlobalStyle";
import ProtectedRoute from "./components/ProtectedRoute";
import VoiceUnderstandingPage from "./pages/results/VoiceUnderstandingPage";
import VoiceHighlightPage from "./pages/results/VoiceHighlightPage";
import RealtimeSttPage from "./pages/results/RealtimeSttPage";
import BillingPage from "./pages/me/BillingPage";
import DocumentPage from "./pages/workspace/ResultsPage";
import ContactPage from "./pages/public/ContactPage";
import TermsPage from "./pages/policies/TermsPage";
import PrivacyPage from "./pages/policies/PrivacyPage";
import { UploadStoreProvider } from "./store/uploadStore";
import ModalRenderer from "./components/modal/ModalRenderer";
import { useNotificationStore } from "./store/notificationStore";
import { useModal } from "./store/modalStore";
import SubtitleGenerationPage from "./pages/results/SubtitleGenerationPage";
import PaymentFormPage from "./pages/me/PaymentFormPage";
import PlanSelectionPage from "./pages/me/PlanSelectionPage";

const NotificationWatcher: React.FC = () => {
  const navigate = useNavigate();
  const { notification, errorNotification, clearNotification, clearErrorNotification } = useNotificationStore();
  const { openConfirm } = useModal();

  console.log("👀 NotificationWatcher render:", { 
    hasNotification: !!notification, 
    hasErrorNotification: !!errorNotification 
  });

  const handleGoToResult = useCallback(() => {
    if (!notification) return;
    const { projectId, documentId, result, projectName } = notification as any;
    navigate(`/results/${projectId}/${documentId}`, { state: { projectName, result } });
    clearNotification();
  }, [notification, navigate, clearNotification]);

  // 업로드 완료 알림 처리
  useEffect(() => {
    if (!notification) return;
    console.log("🔔 Notification detected:", notification);
    openConfirm({
      title: "분석 완료",
      message: "분석이 완료되었습니다. 결과를 확인하시겠습니까?",
      onConfirm: handleGoToResult,
      onCancel: clearNotification,
    });
  }, [notification, openConfirm, handleGoToResult, clearNotification]);

  // 에러 알림 처리
  useEffect(() => {
    if (!errorNotification) return;
    console.log("❌ Error notification detected:", errorNotification);
    openConfirm({
      title: errorNotification.title,
      message: errorNotification.message,
      confirmText: "재시도",
      cancelText: "닫기",
      variant: "danger",
      onConfirm: () => {
        errorNotification.onRetry?.();
        clearErrorNotification();
      },
      onCancel: () => {
        errorNotification.onCancel?.();
        clearErrorNotification();
      },
    });
  }, [errorNotification, openConfirm, clearErrorNotification]);

  return null;
};

const App: React.FC = () => {
  return (
    <>
      <GlobalStyle />
      <ModalRenderer />
      <NotificationWatcher />
      <UploadStoreProvider>
        <Routes>
        <Route path="/" element={<LandingPageWrapper />} />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/mypage"
          element={
            <ProtectedRoute>
              <MyProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/results"
          element={
            <ProtectedRoute>
              <DocumentPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/plan-selection"
          element={
            <ProtectedRoute>
              <PlanSelectionPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payment-form"
          element={
            <ProtectedRoute>
              <PaymentFormPage />
            </ProtectedRoute>
          }
        />

        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Upload Routes */}
        <Route
          path="/upload/:projectId"
          element={
            <ProtectedRoute>
              <FileUploadPage />
            </ProtectedRoute>
          }
        />

        {/* Result Routes */}
        <Route
          path="/results/audion_vu/:documentId"
          element={
            <ProtectedRoute>
              <VoiceUnderstandingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/results/audion_vh/:documentId"
          element={
            <ProtectedRoute>
              <VoiceHighlightPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/results/audion_sg/:documentId"
          element={
            <ProtectedRoute>
              <SubtitleGenerationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/realtime_stt"
          element={
            <ProtectedRoute>
              <RealtimeSttPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      </UploadStoreProvider>
    </>
  );
};

export default App;
